// src/screens/AccountScreen/AccountScreen.jsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { ButtonLarge } from '../../components/common/buttons/ButtonLarge/ButtonLarge';
import { ConfirmModal } from '../../components/common/ConfirmModal/ConfirmModal';
import { LegalModal } from '../../components/common/LegalModal';
import {
  PRIVACY_POLICY_TITLE,
  PRIVACY_POLICY_HTML,
  TERMS_TITLE,
  TERMS_HTML,
  LEGAL_EFFECTIVE_DATE,
} from '../../content/legalContent';
import { signOutUser, deleteUserAccount } from '../../utils/firebase';
import { callApi } from '../../utils/api';

import './account.css';

// Maps Firebase's providerData[0].providerId to the icon we show next to
// the sign-in email. Apple is wired up here even though sign-in with Apple
// isn't exposed on the login screen yet, so this keeps working the moment
// it's turned on — no code change needed on this screen at that point.
const PROVIDER_ICON = {
  'google.com': '/svgs/icon-google.svg',
  'apple.com': '/svgs/icon-apple.svg',
};

export const AccountScreen = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [showResetModal, setShowResetModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState(null);

  const providerIcon = PROVIDER_ICON[user?.provider] || '/svgs/icon-google.svg';

  const handleBack = () => navigate('/dashboard');

  const handleLogout = async () => {
    await signOutUser();
    navigate('/login', { replace: true });
  };

  const handleResetData = async () => {
    if (!user?.uid || isResetting) return;
    setIsResetting(true);
    setError(null);
    try {
      const res = await callApi('/api/reset-quiz-data', {
        method: 'POST',
        body: JSON.stringify({ user_id: user.uid }),
      });
      const data = await res.json();
      if (!data.success) {
        throw new Error(data.message || data.error || 'Reset failed');
      }
      setShowResetModal(false);
      navigate('/dashboard', { replace: true });
    } catch (err) {
      console.error('Reset quiz data failed:', err);
      setError("Couldn't reset quiz data. Please try again.");
    } finally {
      setIsResetting(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!user?.uid || isDeleting) return;
    setIsDeleting(true);
    setError(null);
    try {
      // Delete DB rows first — once the Firebase user is gone below, there's
      // no valid uid left to authenticate a follow-up server call with.
      const res = await callApi('/api/delete-account', {
        method: 'DELETE',
        body: JSON.stringify({ user_id: user.uid }),
      });
      const data = await res.json();
      if (!data.success) {
        throw new Error(data.message || data.error || 'Delete failed');
      }

      await deleteUserAccount();
      navigate('/login', { replace: true });
    } catch (err) {
      console.error('Delete account failed:', err);
      setError("Couldn't delete your account. Please try again.");
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="account-screen">
      <div className="account-content">

        <div className="account-header">
          <h1 className="account-header-title">Account</h1>
          <div className="account-header-line" />
        </div>

        <div className="account-body">

          <div className="account-section">
            <h2 className="account-section-title">Sign-in Method</h2>
            <div className="account-signin-row">
              <img src={providerIcon} alt="" className="account-signin-icon" />
              <span className="account-signin-email">{user?.email}</span>
            </div>
          </div>

          <div className="account-section">
            <div className="account-section-textgroup">
              <h2 className="account-section-title">Reset Quiz Data</h2>
              <p className="account-section-body">
                This will clear your quiz history and stats. Your account will stay signed in — you're just starting your progress over. This can't be undone.
              </p>
            </div>
            <button
              type="button"
              className="account-action-button"
              onClick={() => setShowResetModal(true)}
            >
              Reset Data
            </button>
          </div>

          <div className="account-section">
            <div className="account-section-textgroup">
              <h2 className="account-section-title">Delete Account</h2>
              <p className="account-section-body">
                This permanently deletes your account, sign-in, and all quiz data. This can't be undone.
              </p>
            </div>
            <button
              type="button"
              className="account-action-button"
              onClick={() => setShowDeleteModal(true)}
            >
              Delete Account
            </button>
          </div>

          <div className="account-section">
            <div className="account-section-textgroup">
              <h2 className="account-section-title">Get in Touch</h2>
              <p className="account-section-body">
                Have feedback, found a bug, or want to test RangeDojo on your phone? Get in touch.
              </p>
            </div>
            <a
              href="mailto:joshiagaume@gmail.com"
              className="account-action-button"
            >
              Contact Me
            </a>
          </div>

          <div className="account-section">
            <div className="account-section-textgroup">
              <h2 className="account-section-title">About</h2>
              <p className="account-section-body">
                Range Dojo uses proven learning principles to turn preflop study into lasting recall.
              </p>
              <p className="account-section-body">
                Through structured quizzes, repeated retrieval, and targeted practice, you build the ability to recognize the right decision when it matters.
              </p>
            </div>
          </div>

          <div className="account-section">
            <div className="account-section-textgroup">
              <h2 className="account-section-title">Legal</h2>
            </div>
            <div className="account-legal-buttons">
              <button
                type="button"
                className="account-action-button"
                onClick={() => setShowPrivacyModal(true)}
              >
                Privacy Policy
              </button>
              <button
                type="button"
                className="account-action-button"
                onClick={() => setShowTermsModal(true)}
              >
                Terms & Conditions
              </button>
            </div>
          </div>

          {error && <p className="account-error">{error}</p>}

          <div className="account-bottom-spacer" />

        </div>

        <div className="account-scroll-gradient" aria-hidden="true" />

        <div className="account-footer">
          <ButtonLarge
            text="Back"
            buttonLargeClassName="button-large account-back-button"
            onClick={handleBack}
          />
          <ButtonLarge
            text="Sign Out"
            buttonLargeClassName="button-large account-logout-button"
            onClick={handleLogout}
          />
        </div>

      </div>

      <ConfirmModal
        isOpen={showResetModal}
        title="Are you Sure?"
        message="This can't be undone."
        cancelText="Cancel"
        confirmText="Reset Data"
        onCancel={() => setShowResetModal(false)}
        onConfirm={handleResetData}
        isProcessing={isResetting}
      />

      <ConfirmModal
        isOpen={showDeleteModal}
        title="Are you Sure?"
        message="This can't be undone."
        cancelText="Cancel"
        confirmText="Delete Account"
        onCancel={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteAccount}
        isProcessing={isDeleting}
      />

      <LegalModal
        isOpen={showPrivacyModal}
        title={PRIVACY_POLICY_TITLE}
        effectiveDate={LEGAL_EFFECTIVE_DATE}
        bodyHtml={PRIVACY_POLICY_HTML}
        onClose={() => setShowPrivacyModal(false)}
      />

      <LegalModal
        isOpen={showTermsModal}
        title={TERMS_TITLE}
        effectiveDate={LEGAL_EFFECTIVE_DATE}
        bodyHtml={TERMS_HTML}
        onClose={() => setShowTermsModal(false)}
      />
    </div>
  );
};

export default AccountScreen;
